import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Users,
  BookOpen,
  HeartPulse,
  Phone,
  ArrowRight,
  Shield,
  HelpCircle,
  HeartHandshake,
  Sparkles,
  PenLine,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';

/* Two top-level paths the survivor can choose from. Each path card
 * lists the most relevant onward links so people can find what they
 * need in one tap — without scanning six similar-looking tiles. */
const PATHS = [
  {
    id: 'sv',
    eyebrow: 'PATH 01',
    title: 'Sexual violence',
    blurb:
      'Calm, step-by-step guidance for survivors of rape and sexual violence in England & Wales — whether or not you have reported.',
    accent: 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
    accentSoft: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
    icon: HeartHandshake,
    primary: { to: '/app/sexual-violence', label: 'Start the guide' },
    links: [
      { to: '/app/sexual-violence', label: 'Sexual violence hub' },
      { to: '/app/process', label: 'The judicial process' },
      { to: '/app/rights', label: 'Your rights & ISVAs' },
      { to: '/app/trauma', label: 'Trauma & healing' },
      { to: '/app/support?tab=sv', label: 'Specialist SV helplines' },
    ],
    testid: 'path-sv',
  },
  {
    id: 'da',
    eyebrow: 'PATH 02',
    title: 'Domestic abuse',
    blurb:
      'For anyone experiencing coercive control, financial, emotional, physical or "honour"-based abuse — including same-sex relationships and male survivors.',
    accent: 'linear-gradient(135deg, var(--tf-pink) 0%, var(--tf-purple) 100%)',
    accentSoft: 'linear-gradient(180deg, #FFFFFF 0%, #FBEFF2 100%)',
    icon: Shield,
    primary: { to: '/app/domestic-abuse', label: 'Start the guide' },
    links: [
      { to: '/app/domestic-abuse', label: 'Read the DA guide' },
      { to: '/app/domestic-abuse#safety', label: 'Safety planning' },
      { to: '/app/domestic-abuse#legal', label: 'Coercive control law' },
      { to: '/app/support?tab=da', label: 'Specialist DV helplines' },
    ],
    testid: 'path-da',
  },
];

const MORE = [
  {
    to: '/app/support',
    title: 'All support orgs',
    description: 'Tap-to-call helplines across England & Wales.',
    icon: Users,
    testid: 'more-support',
  },
  {
    to: '/app/journal',
    title: 'Private journal',
    description: 'Stays in your browser. Never uploaded.',
    icon: BookOpen,
    testid: 'more-journal',
  },
  {
    to: '/app/guided-journal',
    title: 'Guided journal',
    description: 'A calm 8-step trauma-informed reflection.',
    icon: Sparkles,
    testid: 'more-guided-journal',
  },
  {
    to: '/app/trauma',
    title: 'Trauma & healing',
    description: 'Why your body reacted — and what helps.',
    icon: HeartPulse,
    testid: 'more-trauma',
  },
  {
    to: '/app/faq',
    title: 'FAQ',
    description: 'Common questions, answered simply.',
    icon: HelpCircle,
    testid: 'more-faq',
  },
];

function PathCard({ path }) {
  const Icon = path.icon;
  return (
    <article
      data-testid={path.testid}
      className="tf-card p-6 sm:p-7 flex flex-col"
      style={{
        background: path.accentSoft,
        borderColor: 'var(--tf-purple-light)',
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{
            background: path.accent,
            color: '#fff',
            boxShadow: '0 10px 24px rgba(139,123,177,0.28)',
          }}
        >
          <Icon size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="font-body font-bold"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.22em',
              color: 'var(--tf-purple)',
            }}
          >
            {path.eyebrow}
          </p>
          <h2
            className="font-heading mt-1"
            style={{
              fontSize: 'clamp(22px, 2.6vw, 26px)',
              color: 'var(--tf-text)',
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
            }}
          >
            {path.title}
          </h2>
        </div>
      </div>

      <p
        className="font-body mt-4"
        style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        {path.blurb}
      </p>

      <ul
        className="mt-5 flex flex-col"
        style={{ borderTop: '1px solid var(--tf-card-border)' }}
      >
        {path.links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              data-testid={`${path.testid}-link-${l.label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '')}`}
              className="flex items-center justify-between gap-3 py-3"
              style={{
                color: 'var(--tf-text)',
                textDecoration: 'none',
                borderBottom: '1px solid var(--tf-card-border)',
              }}
            >
              <span className="font-body font-bold" style={{ fontSize: 14 }}>
                {l.label}
              </span>
              <ArrowRight size={15} color="var(--tf-purple)" />
            </Link>
          </li>
        ))}
      </ul>

      <Link
        to={path.primary.to}
        data-testid={`${path.testid}-primary`}
        className="mt-6 inline-flex items-center justify-center gap-2 py-3 rounded-full font-bold"
        style={{
          background: path.accent,
          color: '#fff',
          textDecoration: 'none',
          fontSize: 14,
          boxShadow: '0 8px 22px rgba(139,123,177,0.28)',
        }}
      >
        {path.primary.label}
        <ArrowRight size={15} />
      </Link>
    </article>
  );
}

export default function Home() {
  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="home-screen">
      <SEO
        title="Home"
        description="Sexual violence and domestic abuse — your calm guide for England, Wales and Scotland. Choose your path: SV (judicial process, your rights, trauma) or DA (coercive control, safety planning, legal protections)."
        path="/app"
      />

      {/* Hero — kept small so the two paths are the first thing people see */}
      <section
        className="tf-card p-6 sm:p-8 text-center tf-fade-up"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F4FB 100%)',
        }}
      >
        <p className="eyebrow">SURVIVOR-BUILT · ENGLAND, WALES &amp; SCOTLAND</p>
        <h1
          className="font-heading mt-3"
          style={{
            fontSize: 'clamp(24px, 3.6vw, 32px)',
            lineHeight: 1.15,
            color: 'var(--tf-text)',
            letterSpacing: '-0.01em',
          }}
        >
          You are believed. You are not alone.
        </h1>
        <p
          className="font-body mt-4 mx-auto"
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            color: 'var(--tf-text-secondary)',
            maxWidth: 520,
          }}
        >
          Choose the path that fits your situation. Everything is anonymous, free, and stays
          in your browser.
        </p>
      </section>

      {/* Crisis banner */}
      <section
        className="mt-6 rounded-2xl p-4 flex items-center gap-3"
        style={{ background: 'var(--tf-purple)' }}
        data-testid="crisis-banner"
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.2)' }}
        >
          <Phone size={16} color="#fff" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-body font-bold text-[13.5px]" style={{ color: '#fff' }}>
            In immediate danger?
          </p>
          <p className="font-body text-[12px]" style={{ color: 'rgba(255,255,255,0.92)' }}>
            Call <a href="tel:999" className="underline" style={{ color: '#fff' }}>999</a> — or{' '}
            <a href="tel:101" className="underline" style={{ color: '#fff' }}>101</a> for non-emergency police.
          </p>
        </div>
        <Link
          to="/app/support"
          data-testid="goto-support-from-banner"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full font-bold"
          style={{
            background: '#fff',
            color: 'var(--tf-purple)',
            fontSize: 12,
            textDecoration: 'none',
          }}
        >
          Help <ArrowRight size={13} />
        </Link>
      </section>

      {/* Two-path picker — the heart of the homepage */}
      <div className="mt-10 mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="eyebrow">CHOOSE YOUR PATH</p>
          <h2
            className="font-heading mt-2"
            style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
          >
            Which one fits you today?
          </h2>
        </div>
        <PrintButton label="Print this overview" />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {PATHS.map((p) => (
          <PathCard key={p.id} path={p} />
        ))}
      </div>

      {/* A note from Angela — editorial link to the Why essay */}
      <Link
        to="/app/why"
        data-testid="home-why-card"
        className="mt-10 block tf-card tf-card-hover p-5 sm:p-6"
        style={{
          textDecoration: 'none',
          color: 'var(--tf-text)',
          background: '#FFFDFA',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
            }}
          >
            <PenLine size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.24em',
                color: 'var(--tf-purple)',
              }}
            >
              A NOTE FROM ANGELA
            </p>
            <h3
              className="font-heading mt-1"
              style={{
                fontSize: 'clamp(18px, 2.2vw, 22px)',
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
                fontFamily: 'Fraunces, Georgia, serif',
                fontWeight: 500,
              }}
            >
              Information, autonomy and informed choice.
            </h3>
            <p
              className="font-body mt-2"
              style={{
                fontSize: 13.5,
                color: 'var(--tf-text-secondary)',
                lineHeight: 1.6,
              }}
            >
              Why I created The Full Impact — and why every survivor should be given clear
              written information they can take away, read privately, and return to at their
              own pace.
            </p>
            <p
              className="font-body mt-3 inline-flex items-center gap-1"
              style={{ fontSize: 13, color: 'var(--tf-purple)', fontWeight: 700 }}
            >
              Read the note <ArrowRight size={14} />
            </p>
          </div>
        </div>
      </Link>

      {/* More resources — calmer, smaller row */}
      <div className="mt-12 mb-4">
        <p className="eyebrow">MORE RESOURCES</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 20, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          For both paths
        </h2>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {MORE.map((m) => {
          const Icon = m.icon;
          return (
            <Link
              key={m.to}
              to={m.to}
              data-testid={m.testid}
              className="tf-card tf-card-hover p-4 flex items-start gap-3"
              style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-purple)' }}
              >
                <Icon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  className="font-heading"
                  style={{
                    fontSize: 15,
                    color: 'var(--tf-text)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {m.title}
                </h3>
                <p
                  className="font-body mt-1"
                  style={{
                    fontSize: 12.5,
                    color: 'var(--tf-text-secondary)',
                    lineHeight: 1.5,
                  }}
                >
                  {m.description}
                </p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Anonymous notice */}
      <div
        className="mt-10 p-4 rounded-2xl flex items-center gap-3"
        style={{ background: 'var(--tf-bg-secondary)', border: '1px solid var(--tf-card-border)' }}
        data-testid="anonymous-notice"
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: '#fff', color: 'var(--tf-purple)' }}
        >
          <ShieldCheck size={18} />
        </div>
        <div>
          <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
            Reading anonymously
          </p>
          <p
            className="font-body"
            style={{ fontSize: 11.5, color: 'var(--tf-text-secondary)', marginTop: 2, lineHeight: 1.5 }}
          >
            No account, no sign-up, no tracking. Anything you write in the journal stays in this browser.
          </p>
        </div>
      </div>
    </div>
  );
}
